/**
 * Takes the fight out of the standing pose.
 *
 * The figures are CC0 rigged characters from a SUPERHERO pack - the mesh
 * inside technician.glb is still called SuperHero_Male - and their idle is
 * posed to match: feet apart, elbows out, fists up, shoulders forward. On a
 * caped figure that reads as ready. On a maintenance technician standing at
 * a pump it reads as someone about to swing at you, which was the first
 * thing anyone said about the crew.
 *
 * THE CLIP CANNOT BE FIXED, ONLY ANSWERED. The file carries four clips -
 * Idle_Loop, Idle_Talking_Loop, Interact, Walk_Loop - and no relaxed idle
 * among them, so there is nothing to switch to. Authoring one is a Blender
 * job and a new asset; this is a correction applied on top of the clip the
 * mixer has just written, every frame, which needs no new art and cannot go
 * stale against one.
 *
 * WHAT IT DOES, and why it is aimed rather than dialled. Naming a rotation
 * per bone is a guess about the rig's local axes that breaks the moment the
 * model is replaced. Instead each arm is AIMED: take the direction the limb
 * actually points in world space, lean it toward straight down by a
 * fraction, and turn the bone by the difference. That is a statement about
 * the result - "the arms hang" - rather than about the skeleton, so it
 * survives a different rig, a mirrored bone or a re-export. The hands are
 * the same idea from the other end: the fingers are slerped back toward
 * their BIND pose, which is the hand the artist modelled open.
 *
 * IT IS GATED ON THE IDLE'S OWN WEIGHT. The correction is scaled by how much
 * Idle_Loop is contributing at that instant, so a walk keeps its arm swing,
 * the interact clip keeps the hand it puts on the panel, and a crossfade
 * between them carries the relaxation in and out with it - no extra timers,
 * and nothing to keep in sync with the clip that is playing.
 */

import * as THREE from 'three'

/** How far toward straight down the upper arm is leaned. */
const UPPER = 0.5
/** And the forearm, which wants to straighten less or the elbow locks. */
const LOWER = 0.4
/** How far the fingers go back toward the bind pose: not all the way. */
const FINGERS = 0.9

const DOWN = new THREE.Vector3(0, -1, 0)
const BONES = ['upperarm', 'lowerarm', 'hand'] as const

/** Scratch, module-level: this runs per person per frame. */
const from = new THREE.Vector3()
const to = new THREE.Vector3()
const dir = new THREE.Vector3()
const aimed = new THREE.Vector3()
const swing = new THREE.Quaternion()
const world = new THREE.Quaternion()
const local = new THREE.Quaternion()

/** The bind rotations of one figure's finger bones, taken before it animates. */
export type RestPose = Map<THREE.Bone, THREE.Quaternion>

/**
 * Record the hand's bind pose. Call once per built figure, BEFORE any clip
 * has played - the loader leaves the skeleton in bind, and once the mixer
 * has run a frame that pose is gone.
 */
export function captureRestPose(model: THREE.Object3D): RestPose {
  const rest: RestPose = new Map()
  model.traverse((o) => {
    if (!/^(index|middle|ring|pinky|thumb)_\d/.test(o.name)) return
    rest.set(o as THREE.Bone, (o as THREE.Bone).quaternion.clone())
  })
  return rest
}

/** Lean one bone's limb direction toward straight down by `t`. */
function relaxLimb(bone: THREE.Object3D, child: THREE.Object3D, t: number): void {
  bone.getWorldPosition(from)
  child.getWorldPosition(to)
  dir.copy(to).sub(from)
  if (dir.lengthSq() < 1e-8) return
  dir.normalize()
  aimed.copy(dir).lerp(DOWN, t).normalize()
  swing.setFromUnitVectors(dir, aimed)
  // The swing is in world terms; the bone's channel is its own. Conjugating
  // by the bone's world rotation is what moves it between the two.
  bone.getWorldQuaternion(world)
  local.copy(world).invert().multiply(swing).multiply(world)
  bone.quaternion.multiply(local)
}

/**
 * Apply the correction to one figure. Call after `mixer.update`, with the
 * weight the idle clip is carrying (0 skips the work entirely).
 */
export function relaxIdlePose(model: THREE.Object3D, rest: RestPose, weight: number): void {
  if (weight <= 0.001) return
  const w = Math.min(1, weight)
  for (const side of ['l', 'r'] as const) {
    const [upper, lower, hand] = BONES.map((b) => model.getObjectByName(`${b}_${side}`))
    if (!upper || !lower || !hand) continue
    relaxLimb(upper, lower, UPPER * w)
    // The forearm is aimed from the shoulder's NEW position, so the upper
    // arm's move has to be committed before it is read.
    upper.updateMatrixWorld(true)
    relaxLimb(lower, hand, LOWER * w)
  }
  const f = FINGERS * w
  for (const [bone, bind] of rest) bone.quaternion.slerp(bind, f)
}
